/**
 * Client contract for the staff-side address autocomplete proxy (`GET /api/v1/address/autocomplete`,
 * `venue-address-and-map`). The backend owns the BAN vendor contract; the SPA only ever sees this
 * normalized shape. `search` is injected into the component so the composing screen wires auth (the
 * bearer token) / the generated client without this feature knowing about either.
 */
import { customInstance } from "@repo/api-client";

export interface AddressSuggestion {
  label: string;
  streetLine1: string;
  locality: string;
  postalCode: string;
  region?: string | null;
  country: string;
  lat: number;
  lng: number;
  providerId: string;
}

export interface AutocompleteResult {
  suggestions: AddressSuggestion[];
  /** BAN unavailable → the UI drops to manual entry (never blocks onboarding). */
  degraded: boolean;
}

export type AddressSearch = (query: string, signal?: AbortSignal) => Promise<AutocompleteResult>;

// Bounded, session-lived cache shared across mounts: typing "10 rue", backspacing, and retyping a prior
// query returns instantly with NO network call (the debounce/abort in VenueLocationSection stays). Only
// non-degraded results are cached (a transient BAN failure must be retryable). Oldest entry evicted at cap.
const CACHE_MAX = 50;
const addressCache = new Map<string, AutocompleteResult>();

const cacheKey = (country: string, query: string): string =>
  `${country} ${query.trim().toLowerCase().replace(/\s+/g, " ")}`;

function withAddressCache(search: AddressSearch, country: string): AddressSearch {
  return async (query, signal) => {
    const key = cacheKey(country, query);
    const hit = addressCache.get(key);
    if (hit) return hit; // synchronous cache hit — signal is moot, nothing is fetched
    const result = await search(query, signal);
    if (!result.degraded) {
      addressCache.set(key, result);
      if (addressCache.size > CACHE_MAX) {
        addressCache.delete(addressCache.keys().next().value as string);
      }
    }
    return result;
  };
}

export interface AddressSearchOptions {
  baseUrl?: string;
  country?: string;
  fetchImpl?: typeof fetch;
}

/**
 * Default fetch-based search over the backend proxy. A non-2xx (incl. a degraded 200 the backend
 * already flags) collapses to `{ suggestions: [], degraded: true }` so the caller only branches on
 * `degraded`. `fetchImpl` is injectable so the page can layer the auth header on top.
 */
export function createAddressSearch(options: AddressSearchOptions = {}): AddressSearch {
  const { baseUrl = "", country = "FR", fetchImpl = fetch } = options;
  const search: AddressSearch = async (query, signal) => {
    const url = `${baseUrl}/api/v1/address/autocomplete?q=${encodeURIComponent(
      query,
    )}&country=${encodeURIComponent(country)}`;
    try {
      const res = await fetchImpl(url, {
        signal,
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return { suggestions: [], degraded: true };
      const body = (await res.json()) as AutocompleteResult;
      return { suggestions: body.suggestions ?? [], degraded: Boolean(body.degraded) };
    } catch {
      return { suggestions: [], degraded: true };
    }
  };
  return withAddressCache(search, country);
}

/**
 * The AUTHED search the app should use: it goes through `@repo/api-client`'s `customInstance`, which
 * targets the API origin (`VITE_API_URL`) and attaches the bearer token — unlike {@link createAddressSearch},
 * whose bare `fetch` hits the SPA's own origin with no auth (fine only when a caller injects both). Errors
 * (401, network, non-2xx) collapse to `{ suggestions: [], degraded: true }`, same contract as the default.
 */
export function createAuthedAddressSearch(country = "FR"): AddressSearch {
  const search: AddressSearch = async (query, signal) => {
    try {
      const body = await customInstance<AutocompleteResult>(
        `/api/v1/address/autocomplete?q=${encodeURIComponent(query)}&country=${encodeURIComponent(country)}`,
        { method: "GET", signal },
      );
      return { suggestions: body.suggestions ?? [], degraded: Boolean(body.degraded) };
    } catch {
      return { suggestions: [], degraded: true };
    }
  };
  return withAddressCache(search, country);
}

const EARTH_RADIUS_M = 6_371_000;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance (metres) between two pins — drives the R3 "significant move" warning. */
export function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat));
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
